import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { MenuItem, Stack, Switch, TextField } from '@mui/material';

import { Table, useTableState } from 'modules/ui/table';
import { Loader } from 'modules/ui/loader';
import { Label } from 'modules/ui/label';
import { dateConfig } from 'modules/app/constants';
import { useMessages } from 'modules/messages/hooks/useMessages';
import { useAuth } from 'modules/auth/hooks/useAuth';

import { FinancialData, FeeStatusEnum, SentDataInterface, Company, MonthlyDailyCoReport } from '../interfaces';
import { PAYMENT_STATUS_COLOR } from '../constants';
import { getAllCompanies, getDailycoReports } from '../hooks/crud';
import { changeCurrency } from '../utils';

export const DailyTrackingList = () => {
  const { t } = useTranslation('common');
  const { show } = useMessages();
  const { toDollar } = changeCurrency();
  const { isSuperAdmin } = useAuth();
  const [dailycoReports, setDailycoReports] = useState<MonthlyDailyCoReport[]>([]);

  const [tableState, onChangeTableState] = useTableState({
    orderBy: [{ direction: 'desc', field: 'date' }],
  });

  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const { data: companies = [] } = useQuery<Company[]>(['companyList'], getAllCompanies);

  const {
    data: fetchedDailycoReports,
    isLoading: isLoadingDailyco,
    isError: isDailycoError,
    refetch: refetchDailyco,
  } = useQuery<MonthlyDailyCoReport[]>(
    ['dailycoReports', selectedCompanyId],
    () => getDailycoReports(selectedCompanyId),
    { enabled: !!selectedCompanyId }
  );
  
  useEffect(() => {
    if (fetchedDailycoReports) {
      setDailycoReports(fetchedDailycoReports);
    }
  }, [fetchedDailycoReports]);

  return (
    <>
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          select
          label={t('daily_tracking.list.filterByCompany')}
          value={selectedCompanyId}
          onChange={e => setSelectedCompanyId(e.target.value)}
          fullWidth
          disabled={!isSuperAdmin}
        >
          <MenuItem value="">{t('daily_tracking.list.allCompanies')}</MenuItem>
          {companies.map(company => (
            <MenuItem key={company.id} value={company.id}>
              {company.business_name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      
      {selectedCompanyId ? (
        <>
          <Loader isLoading={isLoadingDailyco} />

          <Table
            loading={isLoadingDailyco}
            columns={[
              { field: 'period', headerName: t('daily_tracking.list.period'), flex: 1 },
              { field: 'call_count', headerName: t('daily_tracking.list.dailycoCallCount'), flex: 1 },
              { field: 'call_minutes', headerName: t('daily_tracking.list.callMinutes'), flex: 1 },
              {
                field: 'average_call_duration',
                headerName: t('daily_tracking.list.avgCallDuration'),
                valueFormatter: ({ value }) => value?.toFixed(2) + ' min',
                flex: 1,
              },
              {
                field: 'transcribed_minutes',
                headerName: t('daily_tracking.list.transcribedMinutes'),
                valueFormatter: ({ value }) => value?.toFixed(2) + ' min',
                flex: 1,
              },
              {
                field: 'recording_cost_usd',
                headerName: t('daily_tracking.list.recordingCost'),
                valueFormatter: ({ value }) => `$${value?.toFixed(2)}`,
                flex: 1,
              },
              {
                field: 'transcription_cost_usd',
                headerName: t('daily_tracking.list.transcriptionCost'),
                valueFormatter: ({ value }) => `$${value?.toFixed(2)}`,
                flex: 1,
              },
              {
                field: 'total_cost_usd',
                headerName: t('daily_tracking.list.totalCost'),
                valueFormatter: ({ value }) => `$${value?.toFixed(2)}`,
                flex: 1,
              },
            ]}            
            rows={dailycoReports}
            rowCount={dailycoReports.length}
            state={tableState}
            onChangeState={onChangeTableState}
          />
        </>
      ) : (
        <Label text={t('daily_tracking.list.selectCompanyFirst')} color="warning" />
      )}
    </>
  );
};
